import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchStoreUsers } from "../api/queries";
import {
  updateStoreUserRole,
  updateStoreUserDashboardAccess,
  removeStoreUser,
} from "../api/mutations";
import { Menu, Transition } from "@headlessui/react";
import { EllipsisVerticalIcon } from "@heroicons/react/20/solid";
import { Fragment, useState } from "react";
import InviteUser from "../components/InviteUser";
import { useStore } from "../contexts/StoreContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { PlusIcon } from "@heroicons/react/24/outline";
import { BuildingStorefrontIcon } from "@heroicons/react/24/outline";
import DeleteAlert from "../components/DeleteAlert";
import Header from "../components/Header";
import Button from "../components/Button";
import Avatar from "../components/Avatar";

export default function Team() {
  const { selectedStoreId, canManageStore } = useStore();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);

  const {
    data: storeUsers,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["store-users", selectedStoreId],
    queryFn: () => fetchStoreUsers(selectedStoreId),
    enabled: !!selectedStoreId,
  });

  // The badge used to print the raw enum, so every language read OWNER, ADMIN,
  // MEMBER in English while the scanner-only badge beside it was translated.
  //
  // t() hands back the key itself when a translation is missing, and a badge
  // reading "roles.owner" would be worse than the English it replaced -- so a
  // miss falls back to what was there before. That also makes the Tolgee entry
  // and this change independent: whichever ships first, nothing breaks.
  //
  // Not upper-cased: toUpperCase() is wrong in Turkish (i -> I, not İ) and
  // shouts in Greek. The badge keeps its colour, shape and size.
  const roleLabel = (role) => {
    const key = `roles.${role}`;
    const label = t(key);

    return label === key ? role.toUpperCase() : label;
  };

  // Find current user's role in the store
  const currentUserStoreRole = storeUsers?.find(
    (su) => su.user.email === user?.email,
  )?.role;

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const updateRoleMutation = useMutation({
    mutationFn: ({ storeUserId, role }) =>
      updateStoreUserRole({
        storeId: selectedStoreId,
        storeUserId,
        role,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["store-users", selectedStoreId],
      });
    },
  });

  const updateAccessMutation = useMutation({
    mutationFn: ({ storeUserId, dashboardAccess }) =>
      updateStoreUserDashboardAccess({
        storeId: selectedStoreId,
        storeUserId,
        dashboardAccess,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["store-users", selectedStoreId],
      });
    },
  });

  const removeUserMutation = useMutation({
    mutationFn: ({ storeUserId }) =>
      removeStoreUser({
        storeId: selectedStoreId,
        storeUserId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["store-users", selectedStoreId],
      });
    },
  });

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded-sm w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded-sm"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{t("team.error")}</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
        <Header
          icon={BuildingStorefrontIcon}
          title={t("team.title")}
          description={t("team.description")}
          rightSection={
            canManageStore && (
              <Button
                onClick={() => setIsInviteModalOpen(true)}
                icon={PlusIcon}
              >
                {t("team.inviteUser")}
              </Button>
            )
          }
        />

        {/* This is a scroll container — overflow-x-auto makes overflow-y compute
            to auto as well — and past ~5 rows it ends flush with the last row.
            Without the bottom padding the last row's action menu overflows the
            box, which raises a scrollbar, clips the menu, and can oscillate the
            two scrollbars into a render loop. pb-40 keeps the menu inside. */}
        <div
          className="max-w-8xl mx-auto overflow-x-auto pb-40"
          style={{ minHeight: "50vh" }}
        >
          <div className="inline-block min-w-full">
            <table className="min-w-full divide-y divide-gray-300 text-sm text-left whitespace-nowrap">
              <thead className="bg-gray-50">
                <tr className="font-semibold text-gray-900">
                  <th scope="col" className="p-3 pl-4">
                    {t("team.table.name")}
                  </th>
                  <th scope="col" className="p-3">
                    {t("team.table.email")}
                  </th>
                  <th scope="col" className="p-3">
                    {t("team.table.role")}
                  </th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {storeUsers?.map((storeUser) => (
                  <tr key={storeUser.user.id} className="text-gray-500">
                    <td className="p-3 pl-4">
                      <div className="flex items-center">
                        <Avatar
                          src={storeUser.user.avatar}
                          name={storeUser.user.name}
                          className="h-10 w-10 text-sm"
                        />
                        <div className="ml-4 text-gray-900">
                          {storeUser.user.name}
                        </div>
                      </div>
                    </td>
                    <td className="p-3">{storeUser.user.email}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs text-green-700 ring-1 ring-inset ring-green-600/20">
                        {roleLabel(storeUser.role)}
                      </span>
                      {storeUser.dashboard_access === false && (
                        <span className="ml-2 inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-600 ring-1 ring-inset ring-gray-500/10">
                          {t("team.scannerOnly.badge")}
                        </span>
                      )}
                    </td>
                    <td className="relative p-3 text-right">
                      {storeUser.role.toLowerCase() !== "owner" &&
                        (currentUserStoreRole === "admin" ||
                          currentUserStoreRole === "owner") && (
                          <Menu
                            as="div"
                            className="relative inline-block text-left"
                          >
                            <Menu.Button className="p-2 text-gray-500 hover:text-gray-700">
                              <EllipsisVerticalIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </Menu.Button>
                            <Transition
                              as={Fragment}
                              enter="transition ease-out duration-100"
                              enterFrom="transform opacity-0 scale-95"
                              enterTo="transform opacity-100 scale-100"
                              leave="transition ease-in duration-75"
                              leaveFrom="transform opacity-100 scale-100"
                              leaveTo="transform opacity-0 scale-95"
                            >
                              <Menu.Items className="absolute right-0 z-50 mt-2 w-48 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden">
                                {storeUser.role !== "admin" && (
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        onClick={() =>
                                          updateRoleMutation.mutate({
                                            storeUserId: storeUser.id,
                                            role: "admin",
                                          })
                                        }
                                        className={`block px-3 py-1 text-sm leading-6 text-gray-900 w-full text-left ${
                                          active ? "bg-gray-50" : ""
                                        }`}
                                      >
                                        {t("team.promoteToAdmin")}
                                      </button>
                                    )}
                                  </Menu.Item>
                                )}
                                {storeUser.role === "member" && (
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        onClick={() =>
                                          updateAccessMutation.mutate({
                                            storeUserId: storeUser.id,
                                            dashboardAccess:
                                              storeUser.dashboard_access ===
                                              false,
                                          })
                                        }
                                        className={`block px-3 py-1 text-sm leading-6 text-gray-900 w-full text-left ${
                                          active ? "bg-gray-50" : ""
                                        }`}
                                      >
                                        {storeUser.dashboard_access === false
                                          ? t("team.scannerOnly.allowDashboard")
                                          : t("team.scannerOnly.restrict")}
                                      </button>
                                    )}
                                  </Menu.Item>
                                )}
                                {storeUser.role === "admin" && (
                                  <Menu.Item>
                                    {({ active }) => (
                                      <button
                                        onClick={() =>
                                          updateRoleMutation.mutate({
                                            storeUserId: storeUser.id,
                                            role: "member",
                                          })
                                        }
                                        className={`block px-3 py-1 text-sm leading-6 text-gray-900 w-full text-left ${
                                          active ? "bg-gray-50" : ""
                                        }`}
                                      >
                                        {t("team.demoteToMember")}
                                      </button>
                                    )}
                                  </Menu.Item>
                                )}
                                <Menu.Item>
                                  {({ active }) => (
                                    <button
                                      onClick={() => {
                                        setIsDeleteAlertOpen(true);
                                        setDeleteUserId(storeUser.id);
                                      }}
                                      className={`block px-3 py-1 text-sm leading-6 text-red-600 w-full text-left ${
                                        active ? "bg-gray-50" : ""
                                      }`}
                                    >
                                      {t("team.removeUser")}
                                    </button>
                                  )}
                                </Menu.Item>
                              </Menu.Items>
                            </Transition>
                          </Menu>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <InviteUser
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        storeId={selectedStoreId}
      />
      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={() => {
          removeUserMutation.mutate({
            storeUserId: deleteUserId,
          });
          setIsDeleteAlertOpen(false);
        }}
      />
    </div>
  );
}
